/* eslint-disable consistent-return, default-param-last */
import {
    createSelector,
} from 'reselect';

import {
    filterTripsByRangeAndMode,
    searchResultsSelectors,
    sortTrips,
} from 'wanderu-ducks-psearch';
import {
    i18nMoment,
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import {
    extractTemplate,
    isNorthAmericanCountry,
} from 'wanderu-utils';

import {
    selectAppStateSlice,
} from '../../selectors';
import {
    mapBlogLinksToImages,
    mapContent,
    mapCarrierData,
    mapInfoSectionContent,
    mapInfoSectionStructuredData,
    mapPriceHistory,
    remapImageKeyToSrc,
} from '../../utils';
import NAMESPACE from './types';

const BASE_URL = 'https://www.wanderu.com';
const UPCOMING_TRIP_AMOUNT = 5;

function _getAddressRegion(state, country) {
    return isNorthAmericanCountry(country)
        ? state
        : '';
}

function _getStructuredDataConfig(transMode) {
    if (transMode === 'bus') {
        return {
            type: 'BusTrip',
            departureStationKey: 'departureBusStop',
            arrivalStationKey: 'arrivalBusStop',
            stationType: 'BusStop',
        };
    }

    return {
        type: 'TrainTrip',
        departureStationKey: 'departureStation',
        arrivalStationKey: 'arrivalStation',
        stationType: 'TrainStation',
    };
}

function getCityToCityData(appState, id) {
    return selectAppStateSlice(appState)[NAMESPACE][id];
}

const getCityToCityCarrierData = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.carriers,
);

const getContent = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.content,
);

const getNoIndex = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.no_index,
);

const getNoIndexLocale = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.no_index_locale ?? undefined,
);

const getLocationData = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.locations,
);

const getStats = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.stats,
);

const getMetricsData = createSelector(
    getStats,
    (stats) => stats?.metrics,
);

const getDistanceData = createSelector(
    getStats,
    (stats) => stats?.distance,
);

const getPopularStationsData = createSelector(
    getStats,
    (stats) => stats?.mostPopular,
);

const getPriceHistory = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.priceHistory,
);

const getCarrierReviews = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.carrierReviews,
);

const getReviewSummary = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.reviewSummary,
);

const getTimeTableDate = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.timeTableDate,
);

const selectBlogLinks = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.blogLinks,
);

const getCarrierContent = createSelector(
    getCityToCityCarrierData,
    (carrierData) => {
        if (!carrierData) {
            return;
        }

        return carrierData.reduce((acc, carrier) => {
            Object.entries(carrier.content)
                .forEach(([key, value]) => {
                    acc[`${carrier.code}_${key}`] = value;
                });
            return acc;
        }, {});
    },
);

function getBreadcrumbUrls(urlParams) {
    const {
        mode: transMode,
        toCity,
        toRegion,
    } = urlParams;

    if (!toRegion) {
        return;
    }

    const [
        toCountryCode,
    ] = toRegion.split('-');

    return {
        transMode: `/${transMode}-tickets/`,
        toCountry: `/${transMode}-tickets/${toCountryCode}/`,
        toState: `/${transMode}-tickets/${toRegion}/`,
        toCity: `/${transMode}-tickets/${toRegion}/${toCity}/`,
    };
}

function mapCheapestTicketData(data) {
    if (!data) {
        return;
    }

    const {
        cheapestPriceUsd,
        cheapestAveragePriceUsd,
        savingsPriceUsd,
    } = data;

    return {
        cheapestPrice: cheapestPriceUsd,
        cheapestAvgPrice: cheapestAveragePriceUsd,
        savingsPriceUsd: savingsPriceUsd,
    };
}

function mapTravelFrequencyData(data) {
    if (!data) {
        return;
    }

    const {
        busiestDayOfWeek,
        quietestDayOfWeek,
    } = data;

    const {
        short: busiestDayOfWeekAbbr,
    } = busiestDayOfWeek;

    const {
        short: quietestDayOfWeekAbbr,
    } = quietestDayOfWeek;

    return {
        busiestDayOfWeek: busiestDayOfWeekAbbr,
        quietestDayOfWeek: quietestDayOfWeekAbbr,
    };
}

function mapBreadcrumbData(params, content, locationData) {
    if (!content) {
        return;
    }

    const breadcrumbUrls = getBreadcrumbUrls(params);

    const {
        countryCode,
        countryName,
        stateName,
        stateCode,
        name: cityName,
    } = locationData?.arrival || {};

    return {
        transMode: {
            name: content.transModeTickets,
            url: breadcrumbUrls.transMode,
        },
        toCountry: {
            code: countryCode,
            name: countryName,
            url: breadcrumbUrls.toCountry,
        },
        toState: {
            code: isNorthAmericanCountry(countryCode)
                ? stateCode
                : undefined,
            name: isNorthAmericanCountry(countryCode)
                ? stateName
                : undefined,
            url: breadcrumbUrls.toState,
        },
        toCity: {
            name: cityName,
            url: breadcrumbUrls.toCity,
        },
        activeIndex: {
            name: content.fromCityToCity,
        },
    };
}

// no particular mapping needed here
const selectDistanceData = getDistanceData;
const selectLocationData = getLocationData;
const selectMetricsData = getMetricsData;
const selectNoIndex = getNoIndex;
const selectNoIndexLocale = getNoIndexLocale;
const selectReviewSummary = getReviewSummary;
const selectTimeTableDate = getTimeTableDate;

const selectLongFormContent = createSelector(
    getContent,
    extractTemplate,
);

const selectSimplifiedCarrierReviews = createSelector(
    getCarrierReviews,
    (reviews) => (
        reviews?.reduce((simpleReviews, carrierReviews) => ([
            ...simpleReviews,
            ...carrierReviews.individualReviews.map((review) => {
                return {
                    ...review,
                    stats: {
                        ...review.stats,
                        reviewCarrierName: carrierReviews.stats.reviewCarrierName,
                        reviewCarrierGuideSlug: carrierReviews.carrierGuide,
                    },
                };
            }),
        ]), [])
            ?.sort((a, b) => i18nMoment(b.stats.reviewIndividualDepartDate)
                .diff(a.stats.reviewIndividualDepartDate))
    ),
);

const selectCheapestTicketData = createSelector(
    getMetricsData,
    mapCheapestTicketData,
);

const selectCityToCityCarrierData = createSelector(
    getCityToCityCarrierData,
    mapCarrierData,
);

const selectContent = createSelector(
    getContent,
    getCarrierContent,
    mapContent,
);

const selectDailyTripsData = createSelector(
    getMetricsData,
    (metricsData) => {
        if (!metricsData) {
            return;
        }

        const {
            minDurationMinutes,
            tripsPerDay,
            directTripsPerDay,
            earliestDeparture,
            latestDeparture,
            numberOfCarriers,
            minPriceUsd1,
            minPriceUsd2,
            minPriceUsdReverseLink,
        } = metricsData;

        return {
            avgDailyDirectTrips: directTripsPerDay,
            avgDailyTrips: tripsPerDay,
            carriers: numberOfCarriers,
            latestDeparture: latestDeparture,
            earliestDeparture: earliestDeparture,
            minDuration: minDurationMinutes,
            otherTransModePricing: {
                price1Usd: minPriceUsd1,
                price2Usd: minPriceUsd2,
                priceReverseUsd: minPriceUsdReverseLink,
            },
        };
    },
);

const selectPopularStationsData = createSelector(
    getPopularStationsData,
    (popularStationsData) => {
        if (!popularStationsData?.stations) {
            return;
        }

        const mainArrive = {
            ...popularStationsData?.arrive?.main?.content,
            images: popularStationsData?.arrive?.main?.images,
        };
        const mainDepart = {
            ...popularStationsData?.depart?.main?.content,
            images: popularStationsData?.depart?.main?.images,
        };

        return {
            arrive: popularStationsData?.stations?.arrival,
            mainArrive,
            depart: popularStationsData?.stations?.departure,
            mainDepart,
        };
    },
);

const selectCarrierLinks = createSelector(
    getCityToCityCarrierData,
    (carrierData) => {
        if (!carrierData) {
            return;
        }

        return carrierData.map((data) => {
            const {
                code: carrierCode,
                carrierName,
                carrierGuide,
                metrics,
            } = data;

            return {
                carrierName,
                carrierCode,
                averageDailyTrips: metrics.averageDailyTrips,
                minPrice: metrics.minPriceUsd,
                url: `/carrier/${carrierGuide}`,
            };
        });
    },
);

const selectQuickviewData = createSelector(
    getMetricsData,
    (metricsData) => {
        if (!metricsData) {
            return;
        }

        const {
            cheapestPriceUsd,
            minDurationMinutes,
            tripsPerDay,
            numberOfCarriers,
        } = metricsData;

        return [
            {
                name: 'cheapestPrice',
                type: 'currency',
                value: cheapestPriceUsd,
            }, {
                name: 'fastestTime',
                type: 'duration',
                value: minDurationMinutes,
            }, {
                name: 'tripsPerDay',
                type: 'number',
                value: tripsPerDay,
            }, {
                name: 'carrierCount',
                type: 'number',
                value: numberOfCarriers,
            }];
    },
);

const selectJourneyStats = createSelector(
    getMetricsData,
    (metricsData) => {
        if (!metricsData) {
            return;
        }

        const {
            directTripsPerDay,
            minDurationMinutes,
            numberOfCarriers,
            cheapestPriceUsd,
            mostOptionsCarrierName,
        } = metricsData;

        return {
            numberOfCarriers,
            directTripsPerDay,
            minDurationMinutes,
            cheapestPrice: cheapestPriceUsd,
            mostOptionsCarrierName,
        };
    },
);

const selectTravelFrequencyData = createSelector(
    getMetricsData,
    mapTravelFrequencyData,
);

const selectBreadcrumbData = createSelector(
    (appState, c2cId, params) => params,
    getContent,
    getLocationData,
    mapBreadcrumbData,
);

function selectAlternateTransModes(appState, c2cId) {
    const c2cData = getCityToCityData(appState, c2cId);

    return c2cData
        ? c2cData.alternate_trans_modes
        : undefined;
}

const selectAltTransModeStats = createSelector(
    getCityToCityData,
    (c2cData) => c2cData?.alternative_transportation_mode_statistics,
);

const selectInfoSectionContent = createSelector(
    selectContent,
    mapInfoSectionContent,
);

const _selectTravelActionStructuredData = createSelector(
    selectContent,
    selectDistanceData,
    selectLocationData,
    (content, distanceData, locationData) => {
        if (!content || !distanceData || !locationData) {
            return;
        }

        const departLocation = locationData.departure;
        const arriveLocation = locationData.arrival;

        return {
            '@context': 'https://schema.org',
            '@type': 'TravelAction',
            distance: content.distanceSchema,
            fromLocation: {
                '@type': 'Place',
                address: {
                    '@type': 'PostalAddress',
                    addressLocality: departLocation.name,
                    addressRegion: _getAddressRegion(
                        departLocation.stateCode,
                        departLocation.countryCode,
                    ),
                    addressCountry: departLocation.countryCode,
                },
            },
            toLocation: {
                '@type': 'Place',
                address: {
                    '@type': 'PostalAddress',
                    addressLocality: arriveLocation.name,
                    addressRegion: _getAddressRegion(arriveLocation.stateCode, arriveLocation.countryCode),
                    addressCountry: arriveLocation.countryCode,
                },
            },
        };
    },
);

const _selectProductStructuredData = createSelector(
    (appState, c2cId, params, transMode) => transMode,
    selectContent,
    selectCurrency,
    selectLocationData,
    selectMetricsData,
    selectReviewSummary,
    (
        transMode,
        content,
        currency,
        locationData,
        metricsData,
        reviewSummaryData,
    ) => {
        if (!content || !locationData || !metricsData) {
            return;
        }

        const config = _getStructuredDataConfig(transMode);

        let productStructuredData = {
            '@context': 'http://schema.org',
            '@type': 'Product',
            name: content.productName,
            category: {
                '@type': config.type,
            },
            offers: {
                '@type': 'AggregateOffer',
                priceCurrency: currency,
                lowPrice: metricsData.cheapestPriceUsd,
                offerCount: metricsData.tripsPerDay,
            },
        };

        if (reviewSummaryData.overall && reviewSummaryData.overall >= 4) {
            productStructuredData = {
                ...productStructuredData,
                aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: reviewSummaryData.overall.toFixed(1),
                    bestRating: 5,
                    reviewCount: reviewSummaryData.reviewCount,
                },
            };
        }

        return productStructuredData;
    },
);

const _selectBreadCrumbStructuredData = createSelector(
    (appState, c2cId, params, transMode) => params,
    getContent,
    selectBreadcrumbData,
    (params, content, breadcrumbData) => {
        if (!breadcrumbData) {
            return;
        }

        const {
            locale,
        } = params;

        const itemListElement = Object.keys(breadcrumbData)
            .filter((key) => breadcrumbData[key].name)
            .map((key, index) => {
                const {
                    name,
                    url,
                } = breadcrumbData[key];

                const seoURL = key === 'activeIndex'
                    ? `${BASE_URL}${content.canonicalSlug}`
                    : `${BASE_URL}/${locale}${url}`;

                return {
                    '@type': 'ListItem',
                    position: index + 1,
                    name,
                    item: seoURL,
                };
            });

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement,
        };
    },
);

const selectPriceHistory = createSelector(
    getPriceHistory,
    mapPriceHistory,
);

const _selectInfoSectionStructuredData = createSelector(
    getContent,
    selectCheapestTicketData,
    selectDailyTripsData,
    selectPriceHistory,
    mapInfoSectionStructuredData,
);

const selectStructuredData = createSelector(
    _selectTravelActionStructuredData,
    _selectProductStructuredData,
    _selectBreadCrumbStructuredData,
    _selectInfoSectionStructuredData,
    (travelAction, product, breadcrumbs, infoSection) => [
        travelAction,
        product,
        breadcrumbs,
        infoSection,
    ].filter(Boolean),
);

function mapUpcomingTrips(mode, trips, scores) {
    if (!trips || !trips.length) {
        return;
    }

    const filteredTrips = filterTripsByRangeAndMode(trips, {}, mode);
    return sortTrips(filteredTrips, scores, 'recommended').slice(0, UPCOMING_TRIP_AMOUNT);
}

const selectUpcomingTrips = createSelector(
    (appState, psearchParams, mode) => mode,
    searchResultsSelectors.selectSearchResults,
    searchResultsSelectors.selectTripScores,
    mapUpcomingTrips,
);

const selectBlurbSectionData = createSelector(
    selectLocationData,
    selectBlogLinks,
    selectPopularStationsData,
    (locationData = {}, rawBlogLinks, popularStations = {}) => {
        const blogLinks = rawBlogLinks || [];
        const {
            departure,
            arrival,
        } = locationData;

        const {
            arrive: arriveStations,
            depart: departStations,
            mainDepart,
            mainArrive,
        } = popularStations;

        const departCityImages = remapImageKeyToSrc(departure?.images || []);
        const arriveCityImages = remapImageKeyToSrc(arrival?.images || []);

        const departStationImages = remapImageKeyToSrc(mainDepart?.images || []);
        const arriveStationImages = remapImageKeyToSrc(mainArrive?.images || []);

        const blogPosts = mapBlogLinksToImages(blogLinks);

        return {
            blogPosts,
            departCityImages,
            departStationImages,
            arriveCityImages,
            arriveStationImages,
            arriveStations,
            departStations,
        };
    },
);

const selectCityContent = createSelector(
    getLocationData,
    (locationData = {}) => {
        const {
            departure,
            arrival,
        } = locationData;

        return {
            arriveContent: arrival?.content || {},
            departContent: departure?.content || {},
        };
    },
);

const selectRenderFlags = createSelector(
    selectLocale,
    selectContent,
    selectBlurbSectionData,
    selectCityContent,
    selectPopularStationsData,
    (
        locale,
        content = {},
        blurbSectionData = {},
        cityData = {},
        popularStationsData = {},
    ) => {
        const {
            aboutDepartCitySubtitle,
            aboutArriveCitySubtitle,
            popularStationsTitle,
            popularStationsDepartureTitle,
            popularStationsDepartureMainSubtitle,
            popularStationsDepartureOtherSubtitle,
            popularStationsArrivalTitle,
            popularStationsArrivalMainSubtitle,
            popularStationsArrivalOtherSubtitle,
        } = content;
        const { blogPosts } = blurbSectionData;
        const {
            arriveContent,
            departContent,
        } = cityData;
        const {
            mainArrive: arrivalMainStation,
            mainDepart: departureMainStation,
        } = popularStationsData;

        const showCityBlurbDepartSection = !!departContent?.cityDescription
            && !!aboutDepartCitySubtitle;
        const showCityBlurbArriveSection = !!arriveContent?.cityDescription
            && !!aboutArriveCitySubtitle;

        const showStationBlurbDepartSection = !!popularStationsTitle
            && !!popularStationsDepartureTitle
            && !!popularStationsDepartureMainSubtitle
            && !!popularStationsDepartureOtherSubtitle
            && !!departureMainStation?.popularStationsDepartureDescription;
        const showStationBlurbArriveSection = !!popularStationsTitle
            && !!popularStationsArrivalTitle
            && !!popularStationsArrivalMainSubtitle
            && !!popularStationsArrivalOtherSubtitle
            && !!arrivalMainStation?.popularStationsArrivalDescription;

        const showBlogBlurbSection = locale.includes('en') && !!blogPosts.length;

        return {
            showCityBlurbDepartSection,
            showCityBlurbArriveSection,
            showStationBlurbDepartSection,
            showStationBlurbArriveSection,
            showBlogBlurbSection,
        };
    },
);

const selectBlurbSectionContent = createSelector(
    selectBlurbSectionData,
    selectPopularStationsData,
    (blurbSectionData = {}, stationsData = {}) => {
        const {
            blogPosts,
            departCityImages,
            departStationImages,
            arriveCityImages,
            arriveStationImages,
            arriveStations = [],
            departStations = [],
        } = blurbSectionData;

        return {
            blogPosts,
            departCityImages,
            arriveCityImages,
            departStationImages,
            arriveStationImages,
            departStations,
            departMainStationContent: stationsData.mainDepart?.popularStationsDepartureDescription,
            arriveStations,
            arriveMainStationContent: stationsData.mainArrive?.popularStationsArrivalDescription,
        };
    },
);

function mapBookableTrips(trips, scores) {
    if (!trips || !trips.length) {
        return [];
    }

    return sortTrips(trips, scores, 'recommended');
}

const selectBookableTrips = createSelector(
    searchResultsSelectors.selectSearchResults,
    searchResultsSelectors.selectTripScores,
    mapBookableTrips,
);

export {
    selectAltTransModeStats,
    selectAlternateTransModes,
    selectBlurbSectionContent,
    selectBookableTrips,
    selectBreadcrumbData,
    selectCarrierLinks,
    selectCheapestTicketData,
    selectCityContent,
    selectCityToCityCarrierData,
    selectContent,
    selectDailyTripsData,
    selectDistanceData,
    selectInfoSectionContent,
    selectJourneyStats,
    selectLocationData,
    selectLongFormContent,
    selectMetricsData,
    selectNoIndex,
    selectNoIndexLocale,
    selectPopularStationsData,
    selectPriceHistory,
    selectQuickviewData,
    selectRenderFlags,
    selectReviewSummary,
    selectSimplifiedCarrierReviews,
    selectStructuredData,
    selectTimeTableDate,
    selectTravelFrequencyData,
    selectUpcomingTrips,
};
